<script lang="ts">
  import type { StandardSearchResult } from '$lib/pinecone/types';
  import SearchInput from './SearchInput.svelte';
  import SearchResults from './SearchResults.svelte';
  import debounce from 'lodash/debounce';
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';

  let { loading = $bindable(false) } = $props();
  let searchResults: StandardSearchResult[] | undefined = $state();
  let error: string | null = $state(null);
  let currentSearchTerm = '';
  let currentRequestController: AbortController | null = null;

  async function performSearch(query: string) {
    if (currentRequestController) {
      currentRequestController.abort();
    }
    currentRequestController = new AbortController();
    loading = true;
    error = null;

    try {
      const response = await fetch('/api/search/standard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: query
        }),
        signal: currentRequestController.signal
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();

      if (query === currentSearchTerm) {
        searchResults = data.results;
      }
    } catch (err: any) {
      if (err.name !== 'AbortError' && query === currentSearchTerm) {
        error = err instanceof Error ? err.message : 'Search failed';
      }
    } finally {
      if (query === currentSearchTerm) {
        loading = false;
        currentRequestController = null;
      }
    }
  }
  const debouncedSearch = debounce(performSearch, 300);

  function handleInput(query: string) {
    currentSearchTerm = query.trim();

    if (!currentSearchTerm) {
      searchResults = undefined;
      loading = false;
      error = null;
      debouncedSearch.cancel();
      return;
    }

    loading = true;
    debouncedSearch(currentSearchTerm);
  }

  $effect(() => {
    return () => {
      debouncedSearch.cancel();
      if (currentRequestController) {
        currentRequestController.abort();
      }
    };
  });
</script>

<div class="standard-search">
  <SearchInput
    placeholder="Search... (/ to focus)"
    onSubmit={handleInput}
    onInput={handleInput}
    clearOnSubmit={false}
  />

  <div class="results-container">
    {#if error}
      <p class="error">
        <FontSizeDecorator>{error}</FontSizeDecorator>
      </p>
    {:else if loading}
      <div class="loading-message">
        <div class="loading-indicator" aria-hidden="true"></div>
      </div>
    {:else if searchResults && searchResults.length > 0}
      <SearchResults results={searchResults} />
    {:else if searchResults?.length === 0}
      <p><FontSizeDecorator>No results found</FontSizeDecorator></p>
    {/if}
  </div>
</div>

<style lang="scss">
  @import 'src/scss/variables';
  .standard-search {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .results-container {
    flex: 1;
    overflow-y: auto;
    margin-top: 24px;
  }

  .error {
    color: $text-warning-color;
    margin: 16px 0;
  }
  .loading-message {
    display: flex;
    justify-content: center;
    padding: 32px 0;
  }
  .loading-indicator {
    width: 24px;
    height: 24px;
    border: 2px solid $border-depth-2-color;
    border-top-color: $text-default-color;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
